import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type {
  TaxReportJournalPayload,
  TaxReportRow,
} from "src/api/reports/tax";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";

const formatAmount = (value: number): string => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

type Props = {
  data: TaxReportJournalPayload | undefined;
  isLoading: boolean;
};

const TaxReportTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const totalColSpan = columns.length + 1;

  const headerLabels = [generalT("account"), ...columns.map((c) => String(c.name))];

  const sectionHeaderCellSx = {
    bgcolor: "primary.light",
    py: 1.25,
    px: 2,
  };

  const renderRow = (row: TaxReportRow) => {
    if (row.row_type === "header") {
      return (
        <TableRow key={row.key}>
          <TableCell colSpan={totalColSpan} sx={sectionHeaderCellSx}>
            <Typography
              fontWeight={700}
              fontSize="0.8125rem"
              color="primary.dark"
              textTransform="uppercase"
              letterSpacing="0.03em"
            >
              {row.label}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={row.key} hover>
        <TableCell
          sx={{
            fontWeight: row.is_bold ? 700 : 400,
            fontSize: "0.875rem",
            pl: 2,
          }}
        >
          {row.label}
        </TableCell>
        {columns.map((col, colIdx) => {
          const isTotal = colIdx === columns.length - 1;
          return (
            <TableCell
              key={col.branch_id ?? `col-${colIdx}`}
              align="right"
              sx={{
                fontWeight: row.is_bold ? 700 : 400,
                color: isTotal ? "#101828" : "#344054",
                fontSize: "0.8125rem",
              }}
            >
              {formatAmount(row.amounts[colIdx] ?? 0)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {headerLabels.map((label, idx) => (
            <TableCell key={`${label}-${idx}`} align={idx === 0 ? "left" : "right"}>
              <Typography
                fontSize="12px"
                textTransform="uppercase"
                color="#fff"
                lineHeight="18px"
                fontWeight={500}
              >
                {label}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton
          rowsLength={8}
          cellsLength={headerLabels.length}
        />
      ) : (
        <TableBody>{rows.map(renderRow)}</TableBody>
      )}
    </Table>
  );
};

export default TaxReportTable;
